import useFetch from 'hooks/useFetch'
import { useToast } from 'hooks/useToast'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import {
  LuArrowDownToLine,
  LuCheck,
  LuCloudAlert,
  LuCloudUpload,
  LuLoaderCircle,
  LuMoveLeft,
  LuMoveRight,
  LuRefreshCcw,
  LuSearch,
  LuTriangleAlert,
  LuUpload,
  LuX
} from 'react-icons/lu'

export default function UploadFileModal({
  onClose,
  uploadExcelURL,
  handleRefresh,
  handleAnalyzeExcel
}) {
  const { showToast } = useToast()

  // useFetch(): hook personalizado para realizar solicitudes GET, POST, PUT, DELETE
  const [savedIncidents, saveIncidents] = useFetch()

  // useState()
  const [step, setStep] = useState(1)
  const [file, setFile] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const [loading, setLoading] = useState(0)
  const [analyzedData, setAnalyzedData] = useState(false) // Filas del Excel listas para importar
  const [rowLogs, setRowLogs] = useState([]) // Logs de errores, advertencias o éxito del análisis
  const [processedRowsCount, setProcessedRowsCount] = useState(0) // Total de filas procesadas
  const [insertedRowsCount, setInsertedRowsCount] = useState(0) // Filas nuevas insertadas
  const [duplicatedRowsCount, setDuplicatedRowsCount] = useState(0) // Filas existentes actualizadas
  const [importError, setImportError] = useState(null) // Error durante la importación

  // Guarda el archivo subido
  const uploadDocument = (e) => {
    setFile(e?.target?.files[0])
  }

  // Permite recoger un archivo que se haya arrastrado y soltado sobre un div
  const dropHandler = (ev) => {
    ev.preventDefault() // Evita la acción por defecto (abrir el archivo)

    if (ev.dataTransfer.items) {
      // Accede a los archivos con DataTransferItemList
      if (ev.dataTransfer.items.length > 1) {
        showToast('Por favor, suelta solo un archivo.', 'error')
        return
      }

      const item = ev.dataTransfer.items[0]
      if (item.kind === 'file') {
        const file2 = item.getAsFile()
        setFile(file2)
      }
    } else {
      // Accede a los archivos con DataTransfer
      if (ev.dataTransfer.files.length > 1) {
        showToast('Por favor, suelta solo un archivo.', 'error')
        return
      }

      const file3 = ev.dataTransfer.files[0]
      setFile(file3)
    }
  }

  const dragOverHandler = (ev) => {
    ev.preventDefault() // Evita la acción por defecto (abrir el archivo)
  }

  // CRUD: POST
  const handleUploadExcel = async (url, items, apiCall) => {
    const apiCallPost = {
      url,
      method: 'POST',
      body: items
    }
    await apiCall(apiCallPost)
  }

  // Mostrar mensaje API: DELETE (notificación de éxito o error)
  useEffect(() => {
    if (savedIncidents?.isCalled) {
      if (savedIncidents?.message) {
        setTimeout(() => {
          setProcessedRowsCount(savedIncidents?.data?.records) // Total de registros que se han intentado importar
          setInsertedRowsCount(savedIncidents?.data?.difference) // Registros nuevos importados
          setDuplicatedRowsCount(savedIncidents?.data?.duplicates) // Registros existentes actualizados

          setStep(4) // Avanzamos al paso 4
        }, 5000)
      }

      if (savedIncidents?.error) {
        setTimeout(() => {
          setImportError(
            savedIncidents?.error.toString() ||
              'No se han podido importar los registros...'
          )

          setStep(4) // Avanzamos al paso 4
        }, 5000)
      }
    }
  }, [savedIncidents?.loading])

  return (
    <div
      className="fixed inset-0 z-10 overflow-y-auto bg-base/75"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-center h-screen min-h-screen px-4 pt-4 pb-4 text-center sm:items-end sm:block sm:p-0">
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="relative inline-flex flex-col w-full h-full px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-sm shadow-xl sm:h-5/6 md:h-4/6 rtl:text-right dark:bg-gray-900 sm:my-8 sm:align-middle sm:w-11/12 md:w-10/12 xl:w-8/12 lg:p-8">
          {/* Pasos */}
          <ol className="flex items-center justify-between w-full md:space-x-2 lg:space-x-8">
            {/* Paso 1: Archivo - Subir archivo */}
            <li
              className={`flex items-center space-x-2.5 ${step === 1 ? 'text-blue-600 dark:text-blue-500 animate-pulse' : step > 1 ? 'text-green-500 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}
            >
              {step > 1 ? (
                <span className="flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-green-900">
                  <LuCheck
                    className="text-green-500 dark:text-green-400"
                    size={20}
                  />
                </span>
              ) : (
                <span
                  className={`flex items-center justify-center w-8 h-8 border rounded-full shrink-0 ${step === 1 ? 'border-blue-600 dark:border-blue-500' : 'border-gray-500 dark:border-gray-400'}`}
                >
                  1
                </span>
              )}

              <span
                className={`${step === 1 ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}
              >
                <h3 className="font-medium leading-tight">Archivo</h3>
                <p className="text-sm">Subir archivo</p>
              </span>
            </li>

            {/* Paso 2: Analizar - Analizar archivo */}
            <li
              className={`flex items-center space-x-2.5 ${
                step === 2
                  ? rowLogs?.filter((rowLog) => rowLog?.type === 'error')
                      ?.length > 0
                    ? 'text-red-500 dark:text-red-400 animate-pulse'
                    : 'text-blue-600 dark:text-blue-500 animate-pulse'
                  : step > 2
                    ? 'text-green-500 dark:text-green-400'
                    : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {step > 2 ? (
                <span className="flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-green-900">
                  <LuCheck
                    className="text-green-500 dark:text-green-400"
                    size={20}
                  />
                </span>
              ) : rowLogs?.filter((rowLog) => rowLog?.type === 'error')
                  ?.length > 0 ? (
                <span className="flex items-center justify-center w-8 h-8 bg-red-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-red-900">
                  <LuX className="text-red-500 dark:text-red-400" size={20} />
                </span>
              ) : (
                <span
                  className={`flex items-center justify-center w-8 h-8 border rounded-full shrink-0 ${step === 2 ? 'border-blue-600 dark:border-blue-500' : 'border-gray-500 dark:border-gray-400'}`}
                >
                  2
                </span>
              )}

              <span
                className={`${step === 2 ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}
              >
                <h3 className="font-medium leading-tight">Analizar</h3>
                <p className="text-sm">Analizar archivo</p>
              </span>
            </li>

            {/* Paso 3: Importar - Importar registros */}
            <li
              className={`flex items-center space-x-2.5 ${step === 3 ? 'text-blue-600 dark:text-blue-500 animate-pulse' : step > 3 ? (importError ? 'text-red-500 dark:text-red-400' : 'text-green-500 dark:text-green-400') : 'text-gray-500 dark:text-gray-400'}`}
            >
              {step > 3 ? (
                importError ? (
                  <span className="flex items-center justify-center w-8 h-8 bg-red-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-red-900">
                    <LuX className="text-red-500 dark:text-red-400" size={20} />
                  </span>
                ) : (
                  <span className="flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-green-900">
                    <LuCheck
                      className="text-green-500 dark:text-green-400"
                      size={20}
                    />
                  </span>
                )
              ) : (
                <span
                  className={`flex items-center justify-center w-8 h-8 border rounded-full shrink-0 ${step === 3 ? 'border-blue-600 dark:border-blue-500' : 'border-gray-500 dark:border-gray-400'}`}
                >
                  3
                </span>
              )}

              <span
                className={`${step === 3 ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}
              >
                <h3 className="font-medium leading-tight">Importar</h3>
                <p className="text-sm">Importar registros</p>
              </span>
            </li>

            {/* Paso 4: Resumen - Resumen importación */}
            <li
              className={`flex items-center space-x-2.5 ${step === 4 ? (importError ? 'text-red-500 dark:text-red-400 animate-pulse' : 'text-green-500 dark:text-green-400 animate-pulse') : 'text-gray-500 dark:text-gray-400'}`}
            >
              {step === 4 ? (
                importError ? (
                  <span className="flex items-center justify-center w-8 h-8 bg-red-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-red-900">
                    <LuX className="text-red-500 dark:text-red-400" size={20} />
                  </span>
                ) : (
                  <span className="flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -start-4 ring-4 ring-white dark:ring-gray-900 dark:bg-green-900">
                    <LuCheck
                      className="text-green-500 dark:text-green-400"
                      size={20}
                    />
                  </span>
                )
              ) : (
                <span className="flex items-center justify-center w-8 h-8 border border-gray-500 rounded-full shrink-0 dark:border-gray-400">
                  4
                </span>
              )}

              <span
                className={`${step === 4 ? 'flex flex-col' : 'hidden md:flex md:flex-col'}`}
              >
                <h3 className="font-medium leading-tight">Resumen</h3>
                <p className="text-sm">Resumen importación</p>
              </span>
            </li>
          </ol>

          {/* Barra de progreso */}
          <div className="w-full my-5 bg-gray-200 rounded-full dark:bg-gray-700">
            <div
              className={`${
                rowLogs?.filter((rowLog) => rowLog?.type === 'error')?.length >
                  0 && step === 2
                  ? 'bg-red-500'
                  : 'bg-blue-500'
              } text-xs font-medium text-white text-center p-0.5 leading-none rounded-full`}
              style={{
                width: (() => {
                  switch (step) {
                    case 1:
                      return '25%'
                    case 2:
                      return '50%'
                    case 3:
                      return '75%'
                    case 4:
                      return '100%'
                    default:
                      return '0%'
                  }
                })()
              }}
            >
              {(() => {
                switch (step) {
                  case 1:
                    return '25%'
                  case 2:
                    return '50%'
                  case 3:
                    return '75%'
                  case 4:
                    return '100%'
                  default:
                    return null
                }
              })()}
            </div>
          </div>

          {/* Muestra contenido según el paso actual del usuario */}
          {(() => {
            switch (step) {
              case 1:
                return (
                  <div
                    id="drop_zone"
                    className="flex items-center justify-center w-full grow"
                    onDrop={(e) => dropHandler(e)}
                    onDragOver={(e) => {
                      setDragOver(true)
                      dragOverHandler(e)
                    }}
                    onDragLeave={(e) => {
                      setDragOver(false)
                    }}
                  >
                    <label
                      htmlFor="dropzone-file"
                      className={`flex flex-col items-center justify-center w-full h-full border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600 ${dragOver ? 'bg-gray-100 dark:bg-gray-600' : 'bg-gray-50 dark:bg-gray-700'}`}
                    >
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <LuCloudUpload
                          className="text-gray-500 dark:text-gray-400"
                          size={30}
                        />

                        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                          <span className="font-semibold">
                            Haz clic para subir
                          </span>{' '}
                          o arrastra y suelta
                        </p>

                        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                          Solo archivos Excel (.xlsx)
                        </p>

                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-semibold">
                            {file
                              ? `Archivo subido: ${file?.name}`
                              : 'No se ha subido ningún archivo aún'}
                          </span>
                        </p>
                      </div>

                      {/* Área para soltar un archivo arrastrado */}
                      <input
                        id="dropzone-file"
                        type="file"
                        className="hidden"
                        accept=".xlsx"
                        onChange={(e) => uploadDocument(e)}
                      />
                    </label>
                  </div>
                )
              case 2:
                return (
                  <div className="flex flex-col items-center justify-center w-full h-48 py-5 grow">
                    {loading ? (
                      <p
                        className="flex flex-col-reverse items-center justify-center h-full font-bold dark:text-[#F5F5F5] text-gray-700"
                        title="Cargando..."
                      >
                        <LuLoaderCircle
                          size={32}
                          className="animate-spin text-[#5361AD]"
                        />
                      </p>
                    ) : (
                      <>
                        <div className="flex items-center justify-center">
                          <LuCloudAlert
                            className="text-yellow-500 dark:text-yellow-400"
                            size={50}
                          />
                        </div>

                        <div className="w-full text-left">
                          <p className="my-2 text-gray-500 dark:text-gray-400">
                            ¿Está{' '}
                            <span className="font-semibold">seguro/a</span> de
                            querer importar{' '}
                            <span className="font-semibold">
                              {analyzedData?.length}
                            </span>{' '}
                            siniestros?
                          </p>
                        </div>

                        {/* Lista de logs */}
                        <div
                          id="scrollable-modal-logs"
                          className="flex flex-col w-full p-2 overflow-y-auto text-left border-2 border-gray-300 rounded-lg grow bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                        >
                          {rowLogs ? (
                            <div className="w-full space-y-2">
                              {rowLogs?.map((rowLog, index) => (
                                <div
                                  key={index}
                                  className="flex flex-row items-start text-gray-500 dark:text-gray-400"
                                >
                                  {(() => {
                                    switch (rowLog?.type) {
                                      case 'import':
                                        return (
                                          <div className="pt-0.5">
                                            <LuCheck
                                              className="pr-1 text-green-500 w-fit min-w-fit dark:text-green-400"
                                              size={22}
                                            />
                                          </div>
                                        )
                                      case 'warning':
                                        return (
                                          <div className="pt-px">
                                            <LuTriangleAlert
                                              className="pr-1 text-yellow-500 w-fit min-w-fit dark:text-yellow-400"
                                              size={22}
                                            />
                                          </div>
                                        )
                                      case 'error':
                                        return (
                                          <div className="pt-px">
                                            <LuX
                                              className="pr-1 text-red-500 w-fit min-w-fit dark:text-red-400"
                                              size={22}
                                            />
                                          </div>
                                        )
                                      default:
                                        return (
                                          <div className="pt-px">
                                            <LuCloudAlert
                                              className="pr-1 w-fit min-w-fit"
                                              size={22}
                                            />
                                          </div>
                                        )
                                    }
                                  })()}{' '}
                                  <span className="grow">
                                    {rowLog?.message}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>

                        {/* Resumen de los logs */}
                        <div className="flex flex-row justify-between w-full text-left">
                          <div className="flex flex-row items-center my-2 text-gray-500 dark:text-gray-400">
                            <LuCheck
                              className="pr-1 text-green-500 dark:text-green-400 w-fit min-w-fit"
                              size={22}
                            />
                            {
                              rowLogs?.filter(
                                (rowLog) => rowLog?.type === 'import'
                              )?.length
                            }
                            <span className="hidden pl-1 md:flex">
                              correctos
                            </span>
                          </div>

                          <div className="flex flex-row items-center my-2 text-gray-500 dark:text-gray-400">
                            <LuTriangleAlert
                              className="pr-1 text-yellow-500 dark:text-yellow-400 w-fit min-w-fit"
                              size={22}
                            />
                            {
                              rowLogs?.filter(
                                (rowLog) => rowLog?.type === 'warning'
                              )?.length
                            }
                            <span className="hidden pl-1 md:flex">
                              advertencias
                            </span>
                          </div>

                          <div className="flex flex-row items-center my-2 text-gray-500 dark:text-gray-400">
                            <LuX
                              className="pr-1 text-red-500 dark:text-red-400 w-fit min-w-fit"
                              size={22}
                            />
                            {
                              rowLogs?.filter(
                                (rowLog) => rowLog?.type === 'error'
                              )?.length
                            }
                            <span className="hidden pl-1 md:flex">errores</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )
              case 3:
                return (
                  <div className="flex flex-col items-center justify-center w-full h-48 grow">
                    <div className="flex items-center justify-center">
                      <LuLoaderCircle
                        size={32}
                        className="animate-spin text-[#5361AD]"
                      />
                    </div>

                    <div className="text-center">
                      <p className="text-gray-500 dark:text-gray-400">
                        Importando registros. Esto tomará unos momentos. Gracias
                        por tu paciencia.
                      </p>
                    </div>
                  </div>
                )
              case 4:
                return (
                  <div className="flex flex-col items-start justify-start w-full h-48 py-5 space-y-2 grow">
                    {importError ? (
                      <div className="flex flex-col justify-between w-full space-y-2 text-left">
                        <div className="flex flex-row items-center text-gray-500 dark:text-gray-400">
                          <LuX
                            className="pr-1 text-green-500 dark:text-green-400"
                            size={22}
                          />

                          <span>{importError}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col justify-between w-full space-y-2 text-left">
                        <div className="flex flex-row items-start text-gray-500 dark:text-gray-400">
                          <div className="pt-px">
                            <LuCheck
                              className="pr-1 text-green-500 dark:text-green-400 w-fit min-w-fit"
                              size={22}
                            />
                          </div>

                          {/* Registros procesados */}
                          {processedRowsCount > 1 ? (
                            <span className="grow">
                              ¡Importación completada! Se han procesado un total
                              de {processedRowsCount} registros.
                            </span>
                          ) : (
                            <span className="grow">
                              ¡Importación completada! Se han procesado un total
                              de {processedRowsCount} registro.
                            </span>
                          )}
                        </div>

                        {/* Registros actualizados */}
                        {duplicatedRowsCount > 0 ? (
                          <div className="flex flex-row items-start text-gray-500 dark:text-gray-400">
                            <div className="pt-px">
                              <LuRefreshCcw
                                className="pr-1 text-green-500 dark:text-green-400 w-fit min-w-fit"
                                size={22}
                              />
                            </div>

                            {duplicatedRowsCount > 1 ? (
                              <span className="grow">
                                {duplicatedRowsCount} registros ya existían y
                                fueron actualizados automáticamente.
                              </span>
                            ) : (
                              <span className="grow">
                                {duplicatedRowsCount} registro ya existía y fué
                                actualizado automáticamente.
                              </span>
                            )}
                          </div>
                        ) : null}

                        {/* Registros nuevos */}
                        {insertedRowsCount > 0 ? (
                          <div className="flex flex-row items-start text-gray-500 dark:text-gray-400">
                            <div className="pt-px">
                              <LuArrowDownToLine
                                className="pr-1 text-green-500 dark:text-green-400 w-fit min-w-fit"
                                size={22}
                              />
                            </div>

                            {insertedRowsCount > 1 ? (
                              <span className="grow">
                                {insertedRowsCount} nuevos registros se han
                                agregado exitosamente.
                              </span>
                            ) : (
                              <span className="grow">
                                {insertedRowsCount} nuevo registro se ha
                                agregado exitosamente.
                              </span>
                            )}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                )
              default:
                return null
            }
          })()}

          {/* Botones: Cerrar / Volver / Analizar / Importar / Finalizar / Continuar */}
          <div className="flex flex-row items-center justify-between mt-5 space-x-2">
            <div className="flex items-center justify-between gap-x-4">
              <button
                type="button"
                onClick={() => onClose()}
                className={`${step > 2 && 'invisible'} flex items-center justify-center w-fit px-3 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-gray-500 border border-gray-500 rounded-sm md:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-gray-500 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-gray-500 focus:border-gray-500 dark:focus:border-gray-500`}
              >
                <LuX size={20} />
                <span className="hidden md:flex">Cerrar</span>
              </button>
            </div>

            <div className="flex flex-row space-x-2 sm:items-center sm:justify-between sm:gap-x-4">
              {/* Volver: Solo visible en el paso 2, no aplica a otros pasos */}
              {step === 2 && (
                <button
                  type="button"
                  onClick={() => {
                    // Retrocede un paso
                    setStep((prev) => prev - 1)

                    // Si volvemos del paso 2 al 1, limpiamos los logs y eliminamos el archivo
                    if (step === 2) {
                      setRowLogs([])
                      setFile(null)
                    }
                  }}
                  className={`${step > 2 && 'invisible'} flex items-center justify-center w-fit px-3 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-gray-500 border border-gray-500 rounded-sm md:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-gray-500 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-gray-500 focus:border-gray-500 dark:focus:border-gray-500`}
                >
                  <LuMoveLeft size={20} />
                  <span className="hidden md:flex">Volver</span>
                </button>
              )}

              <button
                type="button"
                onClick={async () => {
                  // Paso 1: Al pulsar 'Analizar', procesamos el archivo Excel para verificar si se puede importar
                  if (step === 1) {
                    setLoading(1) // Mostramos la pantalla de carga
                    setStep(2) // Avanzamos al paso 2

                    let promiseResult = null
                    let promiseError = null

                    // Ejecutamos 'handleAnalyzeExcel' (de 'CirculationViewGrid.js' o 'InjuredViewGrid.js') y esperamos su respuesta, no seguimos hasta que no acabe el análisis
                    await handleAnalyzeExcel(file)
                      .then((result) => (promiseResult = result))
                      .catch((error) => (promiseError = error))

                    // Si hay error en el análisis, lo mostramos en los logs
                    if (promiseError !== null) {
                      setRowLogs([
                        {
                          type: 'error',
                          message: promiseError.message
                        }
                      ])
                    } else {
                      setRowLogs(promiseResult.logs) // Logs de errores, advertencias o éxito
                      setAnalyzedData(promiseResult.data) // Registros listos para importar
                    }

                    setLoading(0) // Ocultamos la pantalla de carga
                  }

                  // Paso 2: Al pulsar 'Importar', intentamos importar el archivo Excel a la base de datos
                  if (step === 2) {
                    setLoading(1) // Mostramos la pantalla de carga
                    setStep(3) // Avanzamos al paso 3

                    // Enviamos los registros al backend para su importación
                    await handleUploadExcel(
                      uploadExcelURL,
                      analyzedData,
                      saveIncidents
                    )

                    setLoading(0) // Ocultamos la pantalla de carga
                  }

                  // Paso 3: Pantalla de carga, sin interacción del usuario, pero si hubiera un botón, avanzaría al paso 4
                  if (step === 3) {
                    setStep(4)
                  }

                  // Paso 4: Al hacer clic en 'Finalizar', se recarga la lista con los registros importados y se cierra el modal
                  if (step === 4) {
                    handleRefresh()
                    onClose()
                  }
                }}
                className={`${step === 3 && 'invisible'} flex items-center justify-center w-fit px-3 py-2 space-x-2 md:w-36 text-sm text-white transition-colors duration-200 bg-accent border border-accent rounded-sm dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-accent dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent`}
                disabled={
                  !file ||
                  rowLogs?.filter((rowLog) => rowLog?.type === 'error')
                    ?.length > 0
                } // Deshabilita el botón 'Analizar' si no hay ningún archivo subido y deshabilita el botón 'Importar' si al analizar el archivo Excel se encuentra algún mensaje de error en los logs
              >
                {(() => {
                  switch (step) {
                    case 1:
                      return (
                        <>
                          <LuSearch size={20} />
                          <span className="hidden md:flex">Analizar</span>
                        </>
                      )
                    case 2:
                      return (
                        <>
                          <LuUpload size={20} />
                          <span className="hidden md:flex">Importar</span>
                        </>
                      )
                    case 4:
                      return (
                        <>
                          <LuCheck size={20} />
                          <span className="hidden md:flex">Finalizar</span>
                        </>
                      )
                    default:
                      return (
                        <>
                          <span className="hidden md:flex">Continuar</span>
                          <LuMoveRight size={20} />
                        </>
                      )
                  }
                })()}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

UploadFileModal.propTypes = {
  onClose: PropTypes.func,
  uploadExcelURL: PropTypes.string,
  handleRefresh: PropTypes.func,
  handleAnalyzeExcel: PropTypes.func
}
